<template>
  <StepModel
    :ui="uiBaseStep"
    :name="STEP_NAMES.identityReview"
    :ui-title="identityReviewTitle"
    :ui-subtitle="subtitle"
    ui-error-message="Please review the fields marked in red"
    ui-button="Confirm"
    :ui-button-text="primaryButtonText"
  >
    <FieldsetFullName />
    <FieldsetBornDate title="Date of birth" label="Date of birth" />
    <!-- <FieldsetBornDate
      :name="FIELDSET_NAMES.bornDateReview"
      title="Date of birth"
      label="Date of birth"
      :presence="showNameFields"
    />-->

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.nationality"
      ui-title="Nationality"
      ui-subtitle="Your nationality is mentioned on the front of your ID"
    >
      <UiWrapper
        :name="FIELD_NAMES.nationality"
        label="Select your nationality"
        :validations="[
          {
            'errorMessage': 'This field must not be empty',
            'condition': notEmptyCondition,
            'name': 'nationality-filled',
          },
        ]"
      >
        <Suggest
          :exact-match="true"
          placeholder="Select your nationality"
          :get-formatted-item="formatMatchedString"
          :get-suggestions="getCountries"
        />+
      </UiWrapper>
    </FieldsetModel>

    <FieldsetEmiratesIdNumber />

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.emiratesIdExpiryDate"
      ui-title="Expiry date"
      ui-subtitle="The expiry date is mentioned at the back of your ID"
    >
      <UiWrapper
        :name="FIELD_NAMES.expiryDate"
        label="Emirates ID Expiry date"
        :validations="[
          {
            'errorMessage': 'Please type a future date',
            'condition': isFutureDateCondition,
            'name': 'expirydate-is-futuredate',
          },
        ]"
      >
        <DateWrapper />
      </UiWrapper>
    </FieldsetModel>
    <div v-if="isSalesAgent">
      <FieldsetModel
        :ui="uiBaseFieldset"
        :name="FIELDSET_NAMES.passportNumberFieldSet"
        ui-title="Passport number"
        ui-subtitle="Providing this will help us make a better decision on your application"
      >
        <UiWrapper
          :name="FIELD_NAMES.passportNumber"
          label="Passport number"
          :validations="[
              {
                'errorMessage': 'Please type a valid passport',
                'condition': patternCondition(ALPHA_NUMERIC),
                'name': 'check-is-passport',
              },
            ]"
        >
          <InputText />
        </UiWrapper>
      </FieldsetModel>

      <FieldsetModel
        :ui="uiBaseFieldset"
        :name="FIELDSET_NAMES.passportExpiryDateFieldSet"
        ui-title="Passport expiry date"
        ui-subtitle="Providing this will help us make a better decision on your application"
      >
        <UiWrapper
          :name="FIELD_NAMES.passportExpiryDate"
          label="Expiry date"
          :validations="[
          {
            'errorMessage': 'Please type a future date',
            'condition': isFutureDateCondition,
            'name': 'passport-expirydate-is-futuredate',
          },
        ]"
        >
          <DateWrapper />
        </UiWrapper>
      </FieldsetModel>
    </div>
    <div v-else>
      <FieldsetModel
        :ui="uiBaseFieldset"
        :name="FIELDSET_NAMES.passportNumberFieldSet"
        ui-title="Passport number (optional)"
        ui-subtitle="Providing this will help us make a better decision on your application"
      >
        <UiWrapper
          :name="FIELD_NAMES.passportNumber"
          label="Passport number"
          optional
          :validations="[
              {
                'errorMessage': 'Please type a valid passport',
                'condition': patternCondition(ALPHA_NUMERIC),
                'name': 'check-is-passport',
              },
            ]"
        >
          <InputText />
        </UiWrapper>
      </FieldsetModel>

      <FieldsetModel
        :ui="uiBaseFieldset"
        :name="FIELDSET_NAMES.passportExpiryDateFieldSet"
        ui-title="Passport expiry date (optional)"
        ui-subtitle="Providing this will help us make a better decision on your application"
      >
        <UiWrapper
          :name="FIELD_NAMES.passportExpiryDate"
          label="Expiry date"
          optional
          :validations="[
          {
            'errorMessage': 'Please type a future date',
            'condition': isFutureDateCondition,
            'name': 'passport-expirydate-is-futuredate',
          },
        ]"
        >
          <DateWrapper />
        </UiWrapper>
      </FieldsetModel>
    </div>

    <FieldsetModel :ui="uiBaseFieldset" :name="FIELDSET_NAMES.gender" ui-title="Gender">
      <UiWrapper :name="FIELD_NAMES.gender">
        <Choice :options="GENDER" />
      </UiWrapper>
    </FieldsetModel>
    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.consentPoliticalOptionDetails"
      ui-title="Are you a Politically Exposed Person (PEP)?"
    >
      <UiWrapper :name="FIELD_NAMES.consentPoliticalOption">
        <Choice
          :options="[
                {
                  value: 'Y',
                  label: 'Yes',
                },
                {
                  value: 'N',
                  label: 'No',
                },
              ]"
        />
      </UiWrapper>

      <UiWrapper :name="FIELD_NAMES.consentPolitical" optional>
        <Expandable
          class="consentStyle"
          :extra-text="`You will be considered a Politically Exposed Person (PEP) if you are a senior official
            in the executive, legislative, administrative, military or judicial branches of the either local/foreign
             governments such as a senior politician, a senior government/judicial/military official, or senior
             executive of a state owned corporation. If you are a senior advisor/closely associated to a PEP
             including an immediate member of the family (e.g. spouse, parent, sibling or child), you will be
             considered as a PEP.`"
        />
      </UiWrapper>
    </FieldsetModel>

    <FieldsetModel :ui="uiBaseFieldset" :name="FIELDSET_NAMES.consent">
      <UiWrapper :name="FIELD_NAMES.consent">
        <CheckWrapper>
          <Expandable
            excerpt="I, hereby consent to..."
            :extra-text="`(i) credit bureau(s) including but not limited to Al Etihad Credit
            Bureau Company and Equifax India (the “Credit Bureau”) disclosing my personal,
            financial and credit information (the “Information”), to Deem Finance
            LLC (“${BRAND.name}”) on an on-going basis.
            (ii) ${BRAND.name} accessing any Credit Bureau’s database to carry out credit
            checks and to obtain Information; and
            (iii) ${BRAND.name} disclosing and furnishing Information pertaining to my
            account(s) and related credit conduct to any Credit Bureau from time to time.
            (iv) ${BRAND.name} requesting and getting bank statements of my account(s)
            from any and all banks that I may have accounts with my bank for my statement
            of account through the Central Bank of the United Arab Emirates, and also for
            my bank to provide any and all information requested by ${BRAND.name} through the
            Central Bank of the United Arab Emirates, without taking additional consent.
            I further agree and understand that the Information is released without any
            liability or responsibility on the part of ${BRAND.name} and that the Information
            provided by ${BRAND.name} to any Credit Bureau will be made available by that
            Credit Bureau to its members.`"
          />
        </CheckWrapper>
      </UiWrapper>
    </FieldsetModel>
    <FieldsetModel :ui="uiBaseFieldset" name="cifErrorFieldSet">
      <p id="cifErrorMessage" ></p>
    </FieldsetModel>
  </StepModel>
</template>

<script>
import FormComponentMixin from "@/lib/mixins/form-component-mixin";
//import getGoogleTagManager from "@/forms/helpers/google-tag-manager";
// Components
import UiWrapper from "@/forms/components/base/ui-wrapper";
import StepModel from "@/lib/components/models/step";
import FieldsetModel from "@/lib/components/models/fieldset";
import FieldsetBornDate from "@/forms/components/shared/fieldsets/born-date";
import FieldsetEmiratesIdNumber from "@/forms/components/shared/fieldsets/emirates-id-number";
import DateWrapper from "@/forms/components/ui-dunia/date-wrapper";
import CheckWrapper from "@/forms/components/ui-dunia/check-wrapper";
import FieldsetFullName from "@/forms/components/shared/fieldsets/full-name";
import FIELD_NAMES from "@/forms/constants/field-names";
// Ui Components
import { Suggest, Choice, Expandable, InputText } from "@dunia/ui";

export default {
  components: {
    UiWrapper,
    StepModel,
    FieldsetModel,
    Suggest,
    Choice,
    DateWrapper,
    CheckWrapper,
    FieldsetBornDate,
    FieldsetFullName,
    FieldsetEmiratesIdNumber,
    Expandable,
    InputText
  },

  mixins: [FormComponentMixin],
  data: () => ({
    identityReviewTitle: "Identity Review"
  }),
  props: {
    showNameFields: {
      type: Boolean,
      default: true
    },
    stepTitle: {
      type: String
    }
  },
  mounted() {
    //getGoogleTagManager();
    var gtagID = process.env.VUE_APP_GA_MEASUREMENT_ID;
    if (gtagID) {
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", `${gtagID}`);
    }
  },
  computed: {
    subtitle() {
      if (this.hasUploadedIDCard) {
        return "Check if the below information matches your Emirates ID. If not, please make changes to ensure that they match";
      }

      return "Enter details exactly as it appears on your Emirates ID";
    },
    isSalesAgent() {
      return this.$store.state.employeeCodes;
    },
    isCifCreated() {
      if (this.$store.state.isCifError) {
        return "An error occured while generating the CIF  number. Please try again by clicking on the below retry button .";
      }
    },
    primaryButtonText() {
      if (this.$store.state.isCifError) {
        return "RETRY";
      } else {
        return "NEXT";
      }
    }
  },
  watch: {
    isCifCreated(value) {
      var p = document.getElementById("cifErrorMessage");
      if (p) {
        p.innerHTML = typeof value !== "undefined" ? value : "";
      }
    }
  }
};
</script>
<style scoped>
.consentStyle {
  font-family: "Moderat", serif;
  font-weight: 300;
  font-size: 15px;
  line-height: 24px;
  letter-spacing: 0px;
  display: inline;
  color: #9e9e9e;
  cursor: pointer;
}
</style>
